import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
/**
 * Role Impersonator Component
 *
 * Allows Super Admins to temporarily view the application as different roles
 * to test permissions and user experience without affecting the actual account.
 */
import { useState } from 'react';
import styled from 'styled-components';
import { FaUserSecret, FaUser, FaUndo, FaEye, FaExclamationTriangle } from 'react-icons/fa';
import { useAuthStore } from '../stores/authStore';
import { RoleLevel, ROLE_LABELS } from '../types/rbacImproved';
const ImpersonatorContainer = styled.div `
  position: fixed;
  bottom: 20px;
  right: 20px;
  z-index: 9999;
  background: ${props => props.$isImpersonating ? '#ef4444' : 'var(--bg-secondary)'};
  border: 2px solid ${props => props.$isImpersonating ? '#dc2626' : 'var(--border-color-hover)'};
  border-radius: 8px;
  padding: 16px;
  min-width: 300px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
  backdrop-filter: blur(10px);

  @media (max-width: 768px) {
    bottom: 10px;
    right: 10px;
    left: 10px;
    min-width: auto;
  }
`;
const Header = styled.div `
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
  padding-bottom: 12px;
  border-bottom: 1px solid var(--border-color);
`;
const Title = styled.h3 `
  margin: 0;
  font-size: 14px;
  font-weight: 600;
  color: var(--text-primary);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  display: flex;
  align-items: center;
  gap: 8px;
`;
const ToggleButton = styled.button `
  background: transparent;
  border: 1px solid var(--border-color);
  color: var(--text-primary);
  padding: 4px 8px;
  font-size: 12px;
  cursor: pointer;
  border-radius: 4px;
  transition: all 0.2s;

  &:hover {
    background: var(--bg-tertiary);
    border-color: var(--border-color-hover);
  }
`;
const ImpersonationWarning = styled.div `
  background: rgba(0, 0, 0, 0.3);
  padding: 12px;
  border-radius: 4px;
  margin-bottom: 12px;
  color: #FFFFFF;
  font-size: 12px;
  font-weight: 500;
  text-align: center;
  border: 1px solid rgba(255, 255, 255, 0.3);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;

  .role-name {
    font-weight: 700;
    text-transform: uppercase;
    color: #FFF;
  }
`;
const OriginalRoleInfo = styled.div `
  font-size: 11px;
  color: var(--text-tertiary);
  margin-bottom: 12px;
  padding: 8px;
  background: var(--bg-tertiary);
  border-radius: 4px;

  strong {
    color: var(--text-primary);
  }
`;
const RoleSelect = styled.select `
  width: 100%;
  padding: 10px 12px;
  background: var(--bg-primary);
  color: var(--text-primary);
  border: 1px solid var(--border-color);
  border-radius: 4px;
  font-size: 13px;
  cursor: pointer;
  margin-bottom: 12px;

  &:focus {
    outline: none;
    border-color: var(--border-color-hover);
  }

  option {
    background: var(--bg-primary);
    color: var(--text-primary);
    padding: 8px;
  }

  optgroup {
    background: var(--bg-secondary);
    color: var(--text-secondary);
    font-weight: 600;
  }
`;
const ButtonGroup = styled.div `
  display: flex;
  gap: 8px;
`;
const Button = styled.button `
  flex: 1;
  padding: 10px 16px;
  font-size: 13px;
  font-weight: 600;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s;
  text-transform: uppercase;
  letter-spacing: 0.5px;

  ${props => {
    if (props.$variant === 'danger') {
        return `
        background: #dc2626;
        color: #FFFFFF;
        border: 1px solid #b91c1c;

        &:hover {
          background: #b91c1c;
          transform: translateY(-1px);
          box-shadow: 0 4px 12px rgba(220, 38, 38, 0.3);
        }
      `;
    }
    return `
      background: #22c55e;
      color: #000000;
      border: 1px solid #16a34a;

      &:hover {
        background: #16a34a;
        transform: translateY(-1px);
        box-shadow: 0 4px 12px rgba(34, 197, 94, 0.3);
      }
    `;
}}

  &:active {
    transform: translateY(0);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }
`;
const CollapseButton = styled.button `
  position: absolute;
  top: -12px;
  right: -12px;
  width: 32px;
  height: 32px;
  background: var(--bg-primary);
  border: 2px solid var(--border-color-hover);
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  color: var(--text-primary);
  transition: all 0.2s;

  &:hover {
    background: var(--bg-tertiary);
    transform: scale(1.1);
  }
`;
const MinimizedFloat = styled.button `
  position: fixed;
  bottom: 20px;
  right: 20px;
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background: ${props => props.$isImpersonating ? '#ef4444' : 'var(--bg-secondary)'};
  border: 2px solid ${props => props.$isImpersonating ? '#dc2626' : 'var(--border-color-hover)'};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
  color: var(--text-primary);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.3);
  z-index: 9999;
  transition: all 0.2s;

  &:hover {
    transform: scale(1.1);
    box-shadow: 0 12px 32px rgba(0, 0, 0, 0.4);
  }

  ${props => props.$isImpersonating && `
    animation: pulse 2s infinite;

    @keyframes pulse {
      0%, 100% { box-shadow: 0 8px 24px rgba(239, 68, 68, 0.4); }
      50% { box-shadow: 0 8px 32px rgba(239, 68, 68, 0.8); }
    }
  `}
`;
export const RoleImpersonator = ({ onRoleChange }) => {
    const authStore = useAuthStore();
    const [isExpanded, setIsExpanded] = useState(false);
    const [selectedRole, setSelectedRole] = useState(RoleLevel.BASIC_DEALER);
    const [originalRole, setOriginalRole] = useState(null);
    const [originalUser, setOriginalUser] = useState(null);
    const isImpersonating = originalRole !== null;
    const currentRole = authStore.user?.roleLevel || RoleLevel.GUEST;
    const actualRole = isImpersonating ? originalRole : currentRole;
    // Only show for Super Admin (check original role if impersonating)
    if (!authStore.user || actualRole !== RoleLevel.SUPER_ADMIN) {
        return null;
    }
    const handleImpersonate = () => {
        if (!authStore.user)
            return;
        // Save original role and user data
        setOriginalRole(authStore.user.roleLevel);
        setOriginalUser({ ...authStore.user });
        // Temporarily modify the user's role in the store
        const impersonatedUser = {
            ...authStore.user,
            roleLevel: selectedRole,
            role: Object.entries(RoleLevel)
                .find(([key, val]) => val === selectedRole && isNaN(Number(key)))?.[0] || 'GUEST',
            _impersonating: true
        };
        // Update the auth store
        authStore.setUser(impersonatedUser);
        // Callback for additional handling
        onRoleChange?.(selectedRole);
        console.log('🎭 Impersonating role:', ROLE_LABELS[selectedRole]);
    };
    const handleRestore = () => {
        if (!originalUser)
            return;
        // Restore original user
        authStore.setUser(originalUser);
        setOriginalRole(null);
        setOriginalUser(null);
        // Callback for additional handling
        onRoleChange?.(RoleLevel.SUPER_ADMIN);
        console.log('✅ Restored Super Admin role');
    };
    if (!isExpanded) {
        return (_jsx(MinimizedFloat, { "$isImpersonating": isImpersonating, onClick: () => setIsExpanded(true), title: isImpersonating ? `Impersonating: ${ROLE_LABELS[currentRole]}` : 'Role Impersonator', children: isImpersonating ? _jsx(FaUserSecret, {}) : _jsx(FaUser, {}) }));
    }
    return (_jsxs(ImpersonatorContainer, { "$isImpersonating": isImpersonating, children: [_jsx(CollapseButton, { onClick: () => setIsExpanded(false), children: "\u00D7" }), _jsx(Header, { children: _jsxs(Title, { children: [_jsx(FaUserSecret, {}), "Role Impersonator"] }) }), isImpersonating ? (_jsxs(_Fragment, { children: [_jsxs(ImpersonationWarning, { children: [_jsx(FaExclamationTriangle, {}), "You are viewing as: ", _jsx("span", { className: "role-name", children: ROLE_LABELS[currentRole] })] }), _jsxs(OriginalRoleInfo, { children: [_jsx("strong", { children: "Original Role:" }), " ", ROLE_LABELS[originalRole], " (Level ", originalRole, ")"] }), _jsxs(Button, { "$variant": "danger", onClick: handleRestore, children: [_jsx(FaUndo, { style: { marginRight: '6px' } }), "Restore Super Admin"] })] })) : (_jsxs(_Fragment, { children: [_jsxs(OriginalRoleInfo, { children: [_jsx("strong", { children: "Current Role:" }), " Super Admin (Level 10)", _jsx("br", {}), _jsx("span", { style: { fontSize: '10px', opacity: 0.8 }, children: "Switch roles to test user experience" })] }), _jsxs(RoleSelect, { value: selectedRole, onChange: (e) => setSelectedRole(Number(e.target.value)), children: [_jsxs("optgroup", { label: "System States", children: [_jsxs("option", { value: RoleLevel.SUSPENDED, children: [ROLE_LABELS[RoleLevel.SUSPENDED], " (L", RoleLevel.SUSPENDED, ")"] }), _jsxs("option", { value: RoleLevel.GUEST, children: [ROLE_LABELS[RoleLevel.GUEST], " (L", RoleLevel.GUEST, ")"] })] }), _jsxs("optgroup", { label: "Dealer Roles", children: [_jsxs("option", { value: RoleLevel.DEALER_APPLICANT, children: [ROLE_LABELS[RoleLevel.DEALER_APPLICANT], " (L", RoleLevel.DEALER_APPLICANT, ")"] }), _jsxs("option", { value: RoleLevel.BASIC_DEALER, children: [ROLE_LABELS[RoleLevel.BASIC_DEALER], " (L", RoleLevel.BASIC_DEALER, ")"] }), _jsxs("option", { value: RoleLevel.VERIFIED_DEALER, children: [ROLE_LABELS[RoleLevel.VERIFIED_DEALER], " (L", RoleLevel.VERIFIED_DEALER, ")"] }), _jsxs("option", { value: RoleLevel.SENIOR_DEALER, children: [ROLE_LABELS[RoleLevel.SENIOR_DEALER], " (L", RoleLevel.SENIOR_DEALER, ")"] })] }), _jsxs("optgroup", { label: "Admin Roles", children: [_jsxs("option", { value: RoleLevel.SALES_MANAGER, children: [ROLE_LABELS[RoleLevel.SALES_MANAGER], " (L", RoleLevel.SALES_MANAGER, ")"] }), _jsxs("option", { value: RoleLevel.PLATFORM_ADMIN, children: [ROLE_LABELS[RoleLevel.PLATFORM_ADMIN], " (L", RoleLevel.PLATFORM_ADMIN, ")"] })] })] }), _jsx(ButtonGroup, { children: _jsxs(Button, { onClick: handleImpersonate, children: [_jsx(FaEye, { style: { marginRight: '6px' } }), "View as Role"] }) }), _jsx("div", { style: {
                            fontSize: '10px',
                            color: 'var(--text-tertiary)',
                            marginTop: '12px',
                            textAlign: 'center',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '4px'
                        }, children: "Changes only affect your view, not your actual account" })] }))] }));
};
export default RoleImpersonator;
